import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type {
  Task, Project, ProjectFolder, Session, Settings, UserProfile,
  ForestTree, TimerState, SessionType, SmartViewId,
} from './types';
import { DEFAULT_SETTINGS, getLevelFromHours } from './types';
import { getSessionDuration, getNextSessionType } from './timer';

// ─── Timer store ─────────────────────────────────────────────────────────────

interface TimerStore {
  timer: TimerState;
  settings: Settings;
  startTimer: (taskId?: string) => void;
  pauseTimer: () => void;
  resumeTimer: () => void;
  skipSession: () => void;
  resetTimer: () => void;
  tickTimer: () => void;
  setSessionType: (type: SessionType) => void;
  updateSettings: (s: Partial<Settings>) => void;
  toggleFocusMode: () => void;
}

const initialTimerState: TimerState = {
  status: 'idle',
  sessionType: 'work',
  elapsed: 0,
  duration: DEFAULT_SETTINGS.workDuration * 60,
  pomodorosThisSet: 0,
  isFocusMode: false,
};

export const useTimerStore = create<TimerStore>()(
  persist(
    (set, get) => ({
      timer: initialTimerState,
      settings: DEFAULT_SETTINGS,

      startTimer: (taskId) => set(s => {
        const duration = getSessionDuration(s.timer.sessionType, s.settings);
        return {
          timer: {
            ...s.timer,
            status: 'running',
            taskId,
            startedAt: Date.now(),
            elapsed: 0,
            duration,
          },
        };
      }),

      pauseTimer: () => set(s => ({ timer: { ...s.timer, status: 'paused' } })),

      resumeTimer: () => set(s => ({
        timer: { ...s.timer, status: 'running', startedAt: Date.now() - s.timer.elapsed * 1000 },
      })),

      skipSession: () => set(s => {
        const { timer, settings } = s;
        const nextType = getNextSessionType(timer.sessionType, timer.pomodorosThisSet, settings);
        const newSet = timer.sessionType === 'work' ? timer.pomodorosThisSet + 1 : timer.pomodorosThisSet;
        return {
          timer: {
            ...timer,
            status: 'idle',
            sessionType: nextType,
            elapsed: 0,
            duration: getSessionDuration(nextType, settings),
            pomodorosThisSet: newSet % settings.longBreakAfter,
          },
        };
      }),

      resetTimer: () => set(s => ({
        timer: {
          ...s.timer,
          status: 'idle',
          elapsed: 0,
          duration: getSessionDuration(s.timer.sessionType, s.settings),
        },
      })),

      tickTimer: () => set(s => {
        if (s.timer.status !== 'running') return s;
        const elapsed = s.timer.elapsed + 1;
        if (elapsed >= s.timer.duration) {
          return { timer: { ...s.timer, status: 'finished', elapsed: s.timer.duration } };
        }
        return { timer: { ...s.timer, elapsed } };
      }),

      setSessionType: (type) => set(s => ({
        timer: {
          ...s.timer,
          sessionType: type,
          status: 'idle',
          elapsed: 0,
          duration: getSessionDuration(type, s.settings),
        },
      })),

      updateSettings: (partial) => set(s => ({
        settings: { ...s.settings, ...partial },
      })),

      toggleFocusMode: () => set(s => ({
        timer: { ...s.timer, isFocusMode: !s.timer.isFocusMode },
      })),
    }),
    {
      name: 'flowfocus-timer',
      storage: createJSONStorage(() => localStorage),
    }
  )
);

// ─── App store (tasks, projects, UI state) ───────────────────────────────────

interface AppStore {
  user: UserProfile | null;
  tasks: Task[];
  projects: Project[];
  folders: ProjectFolder[];
  sessions: Session[];
  forest: ForestTree[];
  activeView: SmartViewId | string; // smart view id or project id
  selectedTaskId: string | null;
  searchQuery: string;
  setUser: (u: UserProfile | null) => void;
  setTasks: (t: Task[]) => void;
  setProjects: (p: Project[]) => void;
  setFolders: (f: ProjectFolder[]) => void;
  setSessions: (s: Session[]) => void;
  setForest: (f: ForestTree[]) => void;
  setActiveView: (v: string) => void;
  setSelectedTask: (id: string | null) => void;
  setSearchQuery: (q: string) => void;
}

export const useAppStore = create<AppStore>()((set) => ({
  user: null,
  tasks: [],
  projects: [],
  folders: [],
  sessions: [],
  forest: [],
  activeView: 'today',
  selectedTaskId: null,
  searchQuery: '',
  setUser: (user) => set({ user }),
  setTasks: (tasks) => set({ tasks }),
  setProjects: (projects) => set({ projects }),
  setFolders: (folders) => set({ folders }),
  setSessions: (sessions) => set({ sessions }),
  setForest: (forest) => set({ forest }),
  setActiveView: (activeView) => set({ activeView }),
  setSelectedTask: (selectedTaskId) => set({ selectedTaskId }),
  setSearchQuery: (searchQuery) => set({ searchQuery }),
}));
